import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { coverFor, formatTime } from "@/lib/api";
import {
  Upload, Trash2, Users, Music2, BadgeCheck, Activity, Wallet, FileWarning,
  Plus, Save, X, Mail, Power, RotateCcw, ShieldCheck, Eye, Download,
  CheckCircle2, AlertTriangle, RefreshCw,
} from "lucide-react";
import { StatusPill } from "@/pages/Dashboard";

export default function Admin() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [stats, setStats] = useState({ customers: 0, tracks: 0, active_licenses: 0, expired_licenses: 0, revenue: 0 });
  const [tab, setTab] = useState("tariffs");
  const [emailStatus, setEmailStatus] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [readinessBusy, setReadinessBusy] = useState(false);
  const [backupBusy, setBackupBusy] = useState(false);

  const loadStats = async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch {}
  };
  const loadEmailStatus = async () => {
    try {
      const { data } = await api.get("/admin/email-status");
      setEmailStatus(data);
    } catch {}
  };
  const loadReadiness = async (showToast = false) => {
    setReadinessBusy(true);
    try {
      const { data } = await api.get("/admin/readiness");
      setReadiness(data);
      if (showToast) {
        data.status === "ready"
          ? toast.success("Serviciile necesare sunt pregatite.")
          : toast.warning("Exista elemente care necesita atentie inainte de lansare.");
      }
    } catch {
      if (showToast) toast.error("Starea serviciilor nu a putut fi verificata.");
    } finally {
      setReadinessBusy(false);
    }
  };
  useEffect(() => { loadStats(); loadEmailStatus(); loadReadiness(); }, []);

  const downloadBackup = async () => {
    setBackupBusy(true);
    try {
      const response = await api.get("/admin/database-backup", { responseType: "blob" });
      const disposition = response.headers["content-disposition"] || "";
      const filename = disposition.match(/filename="?([^"]+)"?/i)?.[1] || "stockmusic-production-backup.zip";
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Backupul bazei de date a fost generat.");
    } catch {
      toast.error("Backupul nu a putut fi generat.");
    } finally {
      setBackupBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 pb-32 pt-8 sm:px-6 lg:px-8">
      <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4A23A]">Operator console</p>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl">{t("admin.title")}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {user?.role === "superadmin" && (
            <button
              type="button"
              data-testid="admin-backup-btn"
              onClick={downloadBackup}
              disabled={backupBusy}
              className="inline-flex items-center gap-2 rounded-full bg-[#D4A23A] px-4 py-2 text-xs font-semibold text-black transition hover:bg-[#E2B85C] disabled:opacity-60"
            >
              <Download size={14} />
              {backupBusy ? "Se genereaza..." : "Descarca backup"}
            </button>
          )}
          <button
          data-testid="admin-maintenance-btn"
          onClick={async () => {
            try {
              const { data } = await api.post("/admin/maintenance/run");
              toast.success(`Mentenanță OK: expirate=${data.expired}, reminders=${data.reminders_sent}`);
              loadStats();
            } catch { toast.error(t("common.error")); }
          }}
          className="rounded-full border border-white/10 px-4 py-2 text-xs text-zinc-300 transition hover:bg-white/5"
        >
          {t("admin.maintenanceRun")}
          </button>
        </div>
      </header>

      {emailStatus && !emailStatus.email_enabled && (
        <div className="mb-6 rounded-2xl border border-amber-400/30 bg-amber-400/10 p-4 text-sm text-amber-50" data-testid="admin-email-warning">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="font-semibold text-white">Emailurile reale nu sunt configurate</p>
              <p className="mt-1 text-xs text-amber-100/80">
                RESEND_API_KEY lipseste. Clientii nu primesc email in inbox; mesajele si atasamentele sunt salvate local in outbox.
              </p>
            </div>
            <code className="break-all rounded-lg bg-black/30 px-2 py-1 text-[11px] text-amber-100">{emailStatus.outbox_dir}</code>
          </div>
        </div>
      )}

      {readiness && (
        <ReadinessPanel
          readiness={readiness}
          busy={readinessBusy}
          onRefresh={() => loadReadiness(true)}
        />
      )}

      <div className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat label={t("admin.revenue")} value={`${stats.revenue.toFixed(0)} RON`} icon={Wallet} />
        <Stat label={t("admin.customers")} value={stats.customers} icon={Users} />
        <Stat label={t("admin.activeLicenses")} value={stats.active_licenses} icon={BadgeCheck} />
        <Stat label={t("admin.expiredLicenses")} value={stats.expired_licenses} icon={FileWarning} />
        <Stat label={t("admin.tracks")} value={stats.tracks} icon={Music2} />
      </div>

      <div className="mb-6 flex flex-wrap gap-1 border-b border-white/5">
        {[
          ["tariffs", t("admin.tabs.tariffs")],
          ["customers", t("admin.tabs.customers")],
          ["licenses", t("admin.tabs.licenses")],
          ["tracks", t("admin.tabs.tracks")],
          ["payments", t("admin.tabs.payments")],
          ["audit", t("admin.tabs.audit")],
        ].map(([id, label]) => (
          <button
            key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)}
            className={`relative px-4 py-3 text-sm transition ${
              tab === id ? "text-white" : "text-zinc-500 hover:text-zinc-300"
            }`}
          >
            {label}
            {tab === id && <span className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-[#D4A23A]" />}
          </button>
        ))}
      </div>

      {tab === "tariffs" && <TariffsTab t={t} />}
      {tab === "customers" && <CustomersTabV2 />}
      {tab === "licenses" && <LicensesTab t={t} onChange={loadStats} />}
      {tab === "tracks" && <TracksTab t={t} onChange={loadStats} />}
      {tab === "payments" && <PaymentsTab t={t} />}
      {tab === "audit" && <AuditTab />}
    </div>
  );
}

function ReadinessPanel({ readiness, busy, onRefresh }) {
  const checks = readiness.checks || {};
  const productionMode = checks.deployment_stage === "production";
  const cardReady = !checks.card_payments_enabled
    || (checks.card_payments_configured && (!productionMode || checks.card_payments_mode === "live"));
  const items = [
    ["Mediu productie", productionMode, productionMode ? "production" : checks.deployment_stage || "necunoscut"],
    ["Baza de date", checks.database, checks.database ? "conectata" : "indisponibila"],
    ["Biblioteca audio", checks.storage, checks.storage ? checks.storage_backend || "disponibila" : "indisponibila"],
    ["Email automat", checks.email_enabled, checks.email_provider || "neconfigurat"],
    ["SmartBill", checks.smartbill_enabled && checks.smartbill_configured, checks.smartbill_enabled ? (checks.smartbill_configured ? "activ" : "incomplet") : "inactiv"],
    ["Plata cu cardul", cardReady, checks.card_payments_enabled ? (checks.card_payments_mode || "incomplet") : "inactiva"],
  ];
  const ready = readiness.status === "ready" && productionMode;

  return (
    <section
      className="mb-8 border-y border-white/10 bg-[#0D0D0D] px-4 py-5 sm:px-5"
      data-testid="admin-readiness-panel"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          {ready
            ? <CheckCircle2 className="mt-0.5 text-emerald-400" size={20} />
            : <AlertTriangle className="mt-0.5 text-amber-400" size={20} />}
          <div>
            <p className="font-semibold text-white">Stare lansare</p>
            <p className="mt-1 text-xs text-zinc-400">
              {ready
                ? "Configuratia obligatorie raspunde corect."
                : "Verifica elementele marcate inainte de primul client real."}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={busy}
          className="inline-flex items-center justify-center gap-2 rounded-md border border-white/10 px-3 py-2 text-xs text-zinc-200 transition hover:bg-white/5 disabled:opacity-60"
        >
          <RefreshCw size={14} className={busy ? "animate-spin" : ""} />
          Verifica din nou
        </button>
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {items.map(([label, ok, detail]) => (
          <div key={label} className="flex min-w-0 items-center justify-between gap-3 rounded-md border border-white/5 bg-black/30 px-3 py-2">
            <span className="truncate text-xs text-zinc-300">{label}</span>
            <span className={`inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold ${ok ? "text-emerald-400" : "text-amber-400"}`}>
              {ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}
              {detail}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-white/5 bg-[#121212] p-4">
      <div className="flex items-center justify-between">
        <span className="truncate text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
        <Icon size={14} className="text-[#D4A23A]" />
      </div>
      <p className="mt-2 font-display text-2xl font-black sm:text-3xl">{value}</p>
    </div>
  );
}

/* ----- Tariffs ----- */
function TariffsTab({ t }) {
  const [items, setItems] = useState([]);

  const load = async () => {
    const { data } = await api.get("/admin/tariffs");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const formatRange = (it) => {
    const unit = it.unit_label || "mp";
    if (it.min_units && it.max_units) return `${it.min_units}-${it.max_units} ${unit}`;
    if (it.min_units) return `peste ${it.min_units} ${unit}`;
    return it.display_name || "-";
  };

  const totalFor = (it, months) => it.standard_totals?.[months] ?? it.standard_totals?.[String(months)] ?? "-";

  return (
    <div>
      <div className="mb-4 rounded-2xl border border-[#D4A23A]/20 bg-[#D4A23A]/10 p-4 text-sm text-zinc-300">
        Tarife importate din tabelul corectat. Calculatorul foloseste automat intervalul potrivit pentru categoria si suprafata/unitatea declarata.
      </div>
      <div className="overflow-auto rounded-2xl border border-white/5 bg-[#121212]">
        <table className="w-full min-w-[920px] text-sm" data-testid="tariffs-table">
          <thead className="border-b border-white/5 text-left text-xs uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="px-4 py-3 font-medium">{t("admin.category")}</th>
              <th className="px-4 py-3 font-medium">Interval</th>
              <th className="px-4 py-3 font-medium">3 luni</th>
              <th className="px-4 py-3 font-medium">6 luni</th>
              <th className="px-4 py-3 font-medium">12 luni</th>
              <th className="px-4 py-3 font-medium">Sezonier/luna</th>
              <th className="px-4 py-3 font-medium">Unitate</th>
              <th className="px-4 py-3 font-medium">{t("admin.active")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                <td className="px-4 py-3">
                  <div className="font-medium text-white">{it.category}</div>
                  <div className="mt-1 max-w-xs text-xs text-zinc-500">{it.description || it.display_name}</div>
                </td>
                <td className="px-4 py-3 text-zinc-300">{formatRange(it)}</td>
                <td className="px-4 py-3 font-mono">{totalFor(it, 3)}</td>
                <td className="px-4 py-3 font-mono">{totalFor(it, 6)}</td>
                <td className="px-4 py-3 font-mono">{totalFor(it, 12)}</td>
                <td className="px-4 py-3 font-mono">{it.seasonal_monthly_price ?? it.daily_price ?? "-"}</td>
                <td className="px-4 py-3 text-zinc-400">{it.unit_label || "mp"}</td>
                <td className="px-4 py-3">{it.is_active ? "Da" : "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TariffEditor({ initial, onCancel, onSave, t }) {
  const [f, setF] = useState(initial);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: typeof v === "object" && v?.target ? v.target.value : v }));
  return (
    <div className="grid gap-3 rounded-xl border border-white/10 bg-[#0A0A0A] p-4 md:grid-cols-3" data-testid="tariff-editor">
      <Input label={t("admin.category")} value={f.category} setValue={set("category")} testid="tariff-category" />
      <Input label={t("admin.description")} value={f.description || ""} setValue={set("description")} testid="tariff-description" />
      <Input label={t("admin.pricePerM2")} type="number" value={f.price_per_m2_year} setValue={(v) => setF((s) => ({ ...s, price_per_m2_year: Number(v?.target?.value ?? v) }))} testid="tariff-price" />
      <Input label={t("admin.minPrice")} type="number" value={f.min_price_year} setValue={(v) => setF((s) => ({ ...s, min_price_year: Number(v?.target?.value ?? v) }))} testid="tariff-min" />
      <label className="flex items-end gap-2 text-sm">
        <input
          data-testid="tariff-active"
          type="checkbox" checked={f.is_active}
          onChange={(e) => setF((s) => ({ ...s, is_active: e.target.checked }))}
          className="accent-[#D4A23A]"
        />
        <span>{t("admin.active")}</span>
      </label>
      <div className="flex items-end justify-end gap-2 md:col-span-3">
        <button onClick={onCancel} className="rounded-full border border-white/10 px-4 py-2 text-xs text-zinc-300 hover:bg-white/5">
          <X size={13} className="mr-1 inline" /> {t("common.cancel")}
        </button>
        <button
          data-testid="tariff-save-btn"
          onClick={() => onSave(f)}
          className="rounded-full bg-[#D4A23A] px-4 py-2 text-xs font-semibold text-black hover:bg-[#E2B85C]"
        >
          <Save size={13} className="mr-1 inline" /> {t("common.save")}
        </button>
      </div>
    </div>
  );
}

function Input({ label, value, setValue, testid, type = "text" }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      <input
        data-testid={testid} type={type} value={value}
        onChange={(e) => setValue(e)}
        className="w-full rounded-lg border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#D4A23A]/40"
      />
    </label>
  );
}

/* ----- Customers ----- */
function CustomersTab({ t }) {
  const [items, setItems] = useState([]);
  const [legacyClient, setLegacyClient] = useState(null);
  const [legacyForm, setLegacyForm] = useState({ expiry_month: "", external_reference: "", note: "" });
  const [busyLegacy, setBusyLegacy] = useState(false);
  const load = async () => {
    const { data } = await api.get("/admin/customers");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openLegacyActivation = (user) => {
    const now = new Date();
    const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    setLegacyClient(user);
    setLegacyForm({
      expiry_month: defaultMonth,
      external_reference: "",
      note: "Client existent - achitat anterior in sistemul vechi.",
    });
  };

  const activateLegacyClient = async (event) => {
    event.preventDefault();
    if (!legacyClient) return;
    setBusyLegacy(true);
    try {
      await api.post("/admin/legacy-clients/activate", {
        user_id: legacyClient.id,
        expiry_month: legacyForm.expiry_month,
        external_reference: legacyForm.external_reference || undefined,
        note: legacyForm.note || undefined,
      });
      toast.success("Client existent activat. Licenta si documentele au fost generate fara plata noua.");
      setLegacyClient(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu am putut activa clientul existent.");
    } finally {
      setBusyLegacy(false);
    }
  };
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-[#D4A23A]/20 bg-[#D4A23A]/10 p-4 text-sm text-zinc-300">
        Pentru clientii existenti: clientul isi creeaza contul si introduce singur datele firmei si punctele de lucru.
        Din admin alegi doar luna de expirare, iar aplicatia emite licenta fara plata noua si fara factura SmartBill.
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/5 bg-[#121212]">
      <table className="w-full min-w-[980px] text-sm">
        <thead className="border-b border-white/5 text-left text-xs uppercase tracking-widest text-zinc-500">
          <tr>
            <th className="px-4 py-3 font-medium">Business</th>
            <th className="px-4 py-3 font-medium">Email</th>
            <th className="px-4 py-3 font-medium">Telefon</th>
            <th className="px-4 py-3 font-medium">Categorie</th>
            <th className="px-4 py-3 font-medium">m²</th>
            <th className="px-4 py-3 font-medium">Licență</th>
            <th className="px-4 py-3 font-medium">Înregistrat</th>
          </tr>
        </thead>
        <tbody>
          {items.map((u) => (
            <tr key={u.id} className="border-b border-white/5 last:border-0">
              <td className="px-4 py-3">
                <div className="font-medium">{u.business_name}</div>
                <div className="text-xs text-zinc-500">{u.contact_person}</div>
              </td>
              <td className="px-4 py-3 text-zinc-400">{u.email}</td>
              <td className="px-4 py-3 font-mono text-xs text-zinc-400">{u.phone}</td>
              <td className="px-4 py-3 text-zinc-400">{u.activity_category}</td>
              <td className="px-4 py-3 font-mono">{u.surface_m2}</td>
              <td className="px-4 py-3">
                {u.active_license ? (
                  <span className="rounded-full bg-[#D4A23A] px-2 py-0.5 text-[10px] uppercase text-black">
                    {u.active_license.license_number}
                  </span>
                ) : (
                  <span className="text-xs text-zinc-500">—</span>
                )}
              </td>
              <td className="px-4 py-3 font-mono text-xs text-zinc-500">{u.created_at?.slice(0, 10)}</td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-zinc-500">Niciun client.</td></tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function CustomersTabV2() {
  const [items, setItems] = useState([]);
  const [legacyClient, setLegacyClient] = useState(null);
  const [legacyForm, setLegacyForm] = useState({ expiry_month: "", external_reference: "", note: "" });
  const [busyLegacy, setBusyLegacy] = useState(false);
  const [purgingTests, setPurgingTests] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/customers");
    setItems(data);
  };

  useEffect(() => { load(); }, []);

  const testEmails = [
    "test@gmail.com",
    "dumitrachecatalin96+stripe2607@gmail.com",
  ];
  const presentTestEmails = items
    .map((item) => item.email?.toLowerCase())
    .filter((email) => testEmails.includes(email));

  const purgeTestAccounts = async () => {
    if (!presentTestEmails.length) return;
    setPurgingTests(true);
    try {
      const { data } = await api.post("/admin/test-accounts/purge", {
        emails: presentTestEmails,
        confirmation: "STERGE CONTURILE TEST",
      });
      toast.success(`${data.deleted.users} conturi de test au fost sterse.`);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Conturile de test nu au putut fi sterse.");
    } finally {
      setPurgingTests(false);
    }
  };

  const openLegacyActivation = (user) => {
    const now = new Date();
    const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    setLegacyClient(user);
    setLegacyForm({
      expiry_month: defaultMonth,
      external_reference: "",
      note: "Client existent - achitat anterior in sistemul vechi.",
    });
  };

  const activateLegacyClient = async (event) => {
    event.preventDefault();
    if (!legacyClient) return;
    setBusyLegacy(true);
    try {
      await api.post("/admin/legacy-clients/activate", {
        user_id: legacyClient.id,
        expiry_month: legacyForm.expiry_month,
        external_reference: legacyForm.external_reference || undefined,
        note: legacyForm.note || undefined,
      });
      toast.success("Client existent activat. Licenta si documentele au fost generate fara plata noua.");
      setLegacyClient(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu am putut activa clientul existent.");
    } finally {
      setBusyLegacy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-[#D4A23A]/20 bg-[#D4A23A]/10 p-4 text-sm text-zinc-300">
        Pentru clientii existenti: clientul isi creeaza contul si introduce singur datele firmei si punctele de lucru.
        Din admin alegi doar luna de expirare, iar aplicatia emite licenta fara plata noua si fara factura SmartBill.
      </div>

      {presentTestEmails.length > 0 && (
        <div className="flex flex-col gap-3 rounded-2xl border border-red-500/30 bg-red-500/10 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-white">Curatare date beta</p>
            <p className="mt-1 text-xs text-red-100/70">
              {presentTestEmails.length} conturi de test confirmate, fara factura SmartBill.
            </p>
          </div>
          <button
            type="button"
            data-testid="purge-test-accounts-btn"
            onClick={purgeTestAccounts}
            disabled={purgingTests}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-red-400/50 px-4 py-2 text-xs font-semibold text-red-100 transition hover:bg-red-500 hover:text-white disabled:opacity-60"
          >
            <Trash2 size={14} />
            {purgingTests ? "Se sterg..." : "Sterge conturile de test"}
          </button>
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-white/5 bg-[#121212]">
        <table className="w-full min-w-[980px] text-sm">
          <thead className="border-b border-white/5 text-left text-xs uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="px-4 py-3 font-medium">Business</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Telefon</th>
              <th className="px-4 py-3 font-medium">Categorie</th>
              <th className="px-4 py-3 font-medium">m²</th>
              <th className="px-4 py-3 font-medium">Licenta</th>
              <th className="px-4 py-3 font-medium">Inregistrat</th>
              <th className="px-4 py-3 font-medium" />
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-b border-white/5 last:border-0">
                <td className="px-4 py-3">
                  <div className="font-medium">{u.business_name}</div>
                  <div className="text-xs text-zinc-500">{u.contact_person}</div>
                </td>
                <td className="px-4 py-3 text-zinc-400">{u.email}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-400">{u.phone}</td>
                <td className="px-4 py-3 text-zinc-400">{u.activity_category}</td>
                <td className="px-4 py-3 font-mono">{u.surface_m2}</td>
                <td className="px-4 py-3">
                  {u.active_license ? (
                    <span className="rounded-full bg-[#D4A23A] px-2 py-0.5 text-[10px] uppercase text-black">
                      {u.active_license.license_number}
                    </span>
                  ) : (
                    <span className="text-xs text-zinc-500">-</span>
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-500">{u.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3 text-right">
                  {!u.active_license && (
                    <button
                      onClick={() => openLegacyActivation(u)}
                      className="rounded-full border border-[#D4A23A]/40 px-3 py-1.5 text-xs font-semibold text-[#D4A23A] transition hover:bg-[#D4A23A] hover:text-black"
                    >
                      Activeaza client existent
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-zinc-500">Niciun client.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {legacyClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <form onSubmit={activateLegacyClient} className="w-full max-w-lg rounded-3xl border border-[#D4A23A]/30 bg-[#121212] p-5 shadow-2xl">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.28em] text-[#D4A23A]">Client existent</p>
                <h3 className="mt-2 font-display text-2xl font-black">Activeaza fara plata noua</h3>
                <p className="mt-2 text-sm text-zinc-400">{legacyClient.business_name} · {legacyClient.email}</p>
              </div>
              <button type="button" onClick={() => setLegacyClient(null)} className="rounded-full border border-white/10 p-2 text-zinc-400 hover:bg-white/5">
                <X size={16} />
              </button>
            </div>

            <label className="mb-4 block">
              <span className="mb-2 block text-xs uppercase tracking-widest text-zinc-500">Luna expirarii licentei deja platite</span>
              <input
                type="month"
                required
                value={legacyForm.expiry_month}
                onChange={(e) => setLegacyForm((f) => ({ ...f, expiry_month: e.target.value }))}
                className="w-full rounded-xl border border-white/10 bg-black px-4 py-3 text-white outline-none focus:border-[#D4A23A]"
              />
              <span className="mt-2 block text-xs text-zinc-500">Licenta va expira in ultima zi calendaristica a lunii selectate.</span>
            </label>

            <label className="mb-4 block">
              <span className="mb-2 block text-xs uppercase tracking-widest text-zinc-500">Referinta veche optionala</span>
              <input
                value={legacyForm.external_reference}
                onChange={(e) => setLegacyForm((f) => ({ ...f, external_reference: e.target.value }))}
                placeholder="Factura veche, chitanta, observatie contabila"
                className="w-full rounded-xl border border-white/10 bg-black px-4 py-3 text-white outline-none focus:border-[#D4A23A]"
              />
            </label>

            <label className="mb-5 block">
              <span className="mb-2 block text-xs uppercase tracking-widest text-zinc-500">Nota interna</span>
              <textarea
                value={legacyForm.note}
                onChange={(e) => setLegacyForm((f) => ({ ...f, note: e.target.value }))}
                rows={3}
                className="w-full rounded-xl border border-white/10 bg-black px-4 py-3 text-white outline-none focus:border-[#D4A23A]"
              />
            </label>

            <button type="submit" disabled={busyLegacy} className="w-full rounded-full bg-[#D4A23A] px-5 py-3 text-sm font-black text-black hover:bg-[#E2B85C] disabled:opacity-60">
              {busyLegacy ? "Se activeaza..." : "Activeaza client existent"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

function adminActivitiesLabel(lic) {
  if (lic?.activities?.length) {
    return lic.activities.map((a) => `${a.space_name ? `${a.space_name} - ` : ""}${a.category} (${a.surface_m2} mp)`).join(", ");
  }
  return lic?.activity_category || "-";
}

function formatAdminDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  return `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
}

/* ----- Licenses ----- */
function LicensesTab({ t, onChange }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const load = async () => {
    const { data } = await api.get("/admin/licenses" + (filter ? `?status=${filter}` : ""));
    setItems(data);
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const action = async (lic, kind) => {
    try {
      await api.post(`/admin/licenses/${lic.id}/${kind}`);
      toast.success(t("common.saved"));
      load(); onChange?.();
    } catch { toast.error(t("common.error")); }
  };

  return (
    <div>
      <div className="mb-4 flex gap-2">
        {["", "active", "expired", "suspended", "revoked"].map((s) => (
          <button
            key={s || "all"} data-testid={`license-filter-${s || "all"}`}
            onClick={() => setFilter(s)}
            className={`rounded-full px-3 py-1.5 text-xs uppercase tracking-wider transition ${
              filter === s ? "bg-[#D4A23A] text-black" : "border border-white/10 text-zinc-400 hover:text-white"
            }`}
          >{s || "toate"}</button>
        ))}
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/5 bg-[#121212]">
        <table className="w-full text-sm" data-testid="admin-licenses-table">
          <thead className="border-b border-white/5 text-left text-xs uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="px-4 py-3 font-medium">Număr</th>
              <th className="px-4 py-3 font-medium">Business</th>
              <th className="px-4 py-3 font-medium">Categorie</th>
              <th className="px-4 py-3 font-medium">m²</th>
              <th className="px-4 py-3 font-medium">Expiră</th>
              <th className="px-4 py-3 font-medium">Stare</th>
              <th className="px-4 py-3 font-medium">Sumă</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {items.map((l) => (
              <tr key={l.id} className="border-b border-white/5 last:border-0">
                <td className="px-4 py-3 font-mono text-xs">{l.license_number}</td>
                <td className="px-4 py-3">{l.business_name}<div className="text-xs text-zinc-500">{l.venue_name || "-"}{l.locations_count ? ` ? punct ${l.location_index}/${l.locations_count}` : ""}</div></td>
                <td className="px-4 py-3 text-zinc-400">{adminActivitiesLabel(l)}</td>
                <td className="px-4 py-3 font-mono">{l.surface_m2}</td>
                <td className="px-4 py-3 font-mono text-xs">{formatAdminDate(l.end_date)}</td>
                <td className="px-4 py-3"><StatusPill status={l.status} /></td>
                <td className="px-4 py-3 font-mono text-xs">{Number(l.amount_paid).toFixed(2)} RON</td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`resend-cert-${l.id}`} onClick={() => action(l, "resend-certificate")} className="mr-1 rounded-full p-1.5 text-zinc-500 hover:bg-white/5 hover:text-white" title="Resend">
                    <Mail size={13} />
                  </button>
                  {l.status === "active" && (
                    <button data-testid={`suspend-${l.id}`} onClick={() => action(l, "suspend")} className="mr-1 rounded-full p-1.5 text-orange-300 hover:bg-orange-500/10" title="Suspend">
                      <Power size={13} />
                    </button>
                  )}
                  {(l.status === "suspended" || l.status === "revoked") && (
                    <button data-testid={`reactivate-${l.id}`} onClick={() => action(l, "reactivate")} className="mr-1 rounded-full p-1.5 text-[#D4A23A] hover:bg-[#D4A23A]/10" title="Reactivate">
                      <RotateCcw size={13} />
                    </button>
                  )}
                  {l.status !== "revoked" && (
                    <button data-testid={`revoke-${l.id}`} onClick={() => action(l, "revoke")} className="rounded-full p-1.5 text-red-400 hover:bg-red-500/10" title="Revoke">
                      <Trash2 size={13} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ----- Tracks (admin upload) ----- */
function TracksTab({ t, onChange }) {
  const [tracks, setTracks] = useState([]);
  const load = async () => {
    const { data } = await api.get("/tracks?limit=500");
    setTracks(data);
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-6">
      <UploadForm onDone={() => { load(); onChange?.(); }} />
      <div className="overflow-x-auto rounded-2xl border border-white/5 bg-[#121212]">
        <table className="w-full text-sm">
          <thead className="border-b border-white/5 text-left text-xs uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="px-4 py-3 font-medium">Track</th>
              <th className="px-4 py-3 font-medium">Genre</th>
              <th className="px-4 py-3 font-medium">Mood</th>
              <th className="px-4 py-3 font-medium">Duration</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {tracks.map((tr) => (
              <tr key={tr.id} className="border-b border-white/5 last:border-0">
                <td className="flex items-center gap-3 px-4 py-3">
                  <img src={coverFor(tr)} alt="" className="h-9 w-9 rounded object-cover" />
                  <div>
                    <div className="font-medium">{tr.title}</div>
                    <div className="text-xs text-zinc-500">{tr.artist}</div>
                  </div>
                </td>
                <td className="px-4 py-3 text-zinc-400">{tr.genre}</td>
                <td className="px-4 py-3 text-zinc-400">{tr.mood}</td>
                <td className="px-4 py-3 font-mono text-xs">{formatTime(tr.duration)}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    data-testid={`admin-delete-track-${tr.id}`}
                    onClick={async () => {
                      if (!window.confirm("Șterge?")) return;
                      await api.delete(`/admin/tracks/${tr.id}`);
                      load(); onChange?.();
                    }}
                    className="rounded-full p-2 text-zinc-500 hover:bg-red-500/10 hover:text-red-400"
                  ><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UploadForm({ onDone }) {
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("StockMusic.ro");
  const [genre, setGenre] = useState("Ambient");
  const [mood, setMood] = useState("ambient");
  const [cover, setCover] = useState("");
  const [duration, setDuration] = useState(0);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const onFile = (f) => {
    if (!f) return;
    setFile(f);
    const a = new Audio();
    a.preload = "metadata";
    a.src = URL.createObjectURL(f);
    a.onloadedmetadata = () => { setDuration(Math.floor(a.duration || 0)); URL.revokeObjectURL(a.src); };
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Audio file required");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("artist", artist);
      fd.append("genre", genre);
      fd.append("mood", mood);
      fd.append("duration", String(duration || 0));
      fd.append("cover_url", cover);
      fd.append("audio", file);
      await api.post("/admin/tracks", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setTitle(""); setFile(null); setDuration(0); setCover("");
      if (ref.current) ref.current.value = "";
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="grid gap-3 rounded-2xl border border-white/5 bg-[#121212] p-5 md:grid-cols-3" data-testid="upload-form">
      <Input label="Title" value={title} setValue={(e) => setTitle(e?.target?.value ?? e)} testid="upload-title" />
      <Input label="Artist" value={artist} setValue={(e) => setArtist(e?.target?.value ?? e)} testid="upload-artist" />
      <Input label="Genre" value={genre} setValue={(e) => setGenre(e?.target?.value ?? e)} testid="upload-genre" />
      <Input label="Mood" value={mood} setValue={(e) => setMood(e?.target?.value ?? e)} testid="upload-mood" />
      <Input label="Cover URL" value={cover} setValue={(e) => setCover(e?.target?.value ?? e)} testid="upload-cover" />
      <Input label="Duration (sec)" type="number" value={duration} setValue={(e) => setDuration(Number(e?.target?.value ?? e))} testid="upload-duration" />
      <div className="md:col-span-3">
        <label className="mb-1 block text-[10px] uppercase tracking-widest text-zinc-500">Audio</label>
        <input
          ref={ref} type="file" accept="audio/*" onChange={(e) => onFile(e.target.files?.[0])}
          data-testid="upload-audio"
          className="text-sm file:mr-3 file:rounded-full file:border-0 file:bg-[#D4A23A] file:px-4 file:py-2 file:text-xs file:font-semibold file:text-black hover:file:bg-[#E2B85C]"
        />
      </div>
      <button type="submit" disabled={busy} data-testid="upload-submit-btn" className="rounded-full bg-[#D4A23A] py-2.5 text-xs font-semibold text-black hover:bg-[#E2B85C] disabled:opacity-60 md:col-span-3">
        <Upload size={13} className="mr-1 inline" /> {busy ? "..." : "Upload track"}
      </button>
    </form>
  );
}

/* ----- Payments ----- */
function PaymentsTab() {
  const [items, setItems] = useState([]);
  const [smartBillTest, setSmartBillTest] = useState(null);
  const [smartBillTesting, setSmartBillTesting] = useState(false);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [previewLoadingId, setPreviewLoadingId] = useState(null);
  const load = async () => { const { data } = await api.get("/admin/payments"); setItems(data); };
  useEffect(() => { load(); }, []);
  const confirmBank = async (p) => {
    try {
      await api.post(`/admin/payments/${p.id}/confirm-bank-transfer`, { bank_reference: p.bank_reference });
      toast.success("Plata confirmată. Licența, contractul și emailul către client au fost generate.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu am putut confirma plata.");
    }
  };
  const previewInvoice = async (p) => {
    setPreviewLoadingId(p.id);
    try {
      const { data } = await api.post(`/admin/payments/${p.id}/smartbill-preview`);
      setInvoicePreview(data);
      toast.success("Previzualizarea a fost generata local. Nu a fost emisa nicio factura.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Previzualizarea nu a putut fi generata.");
    } finally {
      setPreviewLoadingId(null);
    }
  };
  const retryInvoice = async (p) => {
    setPreviewLoadingId(p.id);
    try {
      await api.post(`/admin/payments/${p.id}/retry-smartbill-invoice`);
      toast.success("Factura a fost emisa, iar documentele complete au fost retrimise clientului.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Factura nu a putut fi emisa.");
    } finally {
      setPreviewLoadingId(null);
    }
  };
  const runSmartBillSafeTest = async () => {
    setSmartBillTesting(true);
    try {
      const { data } = await api.post("/admin/smartbill/safe-test");
      setSmartBillTest(data);
      if (data.ok) toast.success("Conexiunea SmartBill este valida. Nu a fost emis niciun document.");
      else toast.error(data.error || "Testul SmartBill a identificat o problema.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Testul SmartBill nu a putut fi rulat.");
    } finally {
      setSmartBillTesting(false);
    }
  };
  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-[#D4A23A]/30 bg-[#12100b] p-4" data-testid="smartbill-safe-test-panel">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-3">
            <ShieldCheck className="mt-0.5 shrink-0 text-[#D4A23A]" size={20} />
            <div>
              <h2 className="font-semibold text-white">Test sigur SmartBill</h2>
              <p className="mt-1 text-xs text-zinc-400">Verifica doar seriile si TVA-ul. Nu emite factura, proforma sau plata si nu trimite nimic in SPV.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={runSmartBillSafeTest}
            disabled={smartBillTesting}
            className="shrink-0 rounded-full bg-[#D4A23A] px-4 py-2 text-xs font-semibold text-black hover:bg-[#E2B85C] disabled:opacity-60"
          >
            {smartBillTesting ? "Se verifica..." : "Ruleaza testul sigur"}
          </button>
        </div>
        {smartBillTest && (
          <div className="mt-4 grid gap-2 border-t border-white/5 pt-4 text-xs sm:grid-cols-2 lg:grid-cols-4">
            <SafeTestValue label="Conexiune" value={smartBillTest.ok ? "Valida" : "Necesita verificare"} ok={smartBillTest.ok} />
            <SafeTestValue label="Seria configurata" value={`${smartBillTest.configured_series || "-"}${smartBillTest.series_found ? " - gasita" : " - negasita"}`} ok={smartBillTest.series_found} />
            <SafeTestValue label="Factura emisa" value="Nu" ok />
            <SafeTestValue label="Trimitere SPV" value="Nu" ok />
            <div className="sm:col-span-2 lg:col-span-4">
              <span className="text-zinc-500">Verificare TVA: </span>
              <span className={smartBillTest.tax_check_ok ? "text-zinc-300" : "text-amber-300"}>
                {smartBillTest.tax_check_ok ? (smartBillTest.tax_names?.join(", ") || "Configuratia a fost citita") : "Necesita verificare inaintea primei facturi"}
              </span>
            </div>
            <div className="sm:col-span-2 lg:col-span-4">
              <span className="text-zinc-500">Payload configurat: </span>
              <span className="text-zinc-300">
                {smartBillTest.configured_vat_payer
                  ? `${smartBillTest.configured_tax_name || "-"}, cota ${smartBillTest.configured_vat_rate ?? "-"}%`
                  : "Firma neplatitoare de TVA; parametrii TVA sunt omisi"}
              </span>
            </div>
            {Object.entries(smartBillTest.checks || {}).map(([name, check]) => (
              <div key={name} className="sm:col-span-2 rounded border border-white/5 bg-black/20 px-3 py-2">
                <span className="font-mono text-zinc-400">GET /{name}</span>
                <span className={check.ok ? "ml-2 text-emerald-300" : "ml-2 text-amber-300"}>
                  HTTP {check.status_code || "eroare"}
                </span>
                {check.error && <p className="mt-1 break-words text-amber-200">{check.error}</p>}
                {check.info && <p className="mt-1 break-words text-emerald-200">{check.info}</p>}
              </div>
            ))}
            {smartBillTest.warnings?.map((warning) => (
              <p key={warning} className="sm:col-span-2 lg:col-span-4 text-amber-200">{warning}</p>
            ))}
          </div>
        )}
      </section>
      {invoicePreview && (
        <section className="rounded-lg border border-emerald-400/25 bg-[#0d1510] p-4" data-testid="smartbill-invoice-preview">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-semibold text-white">Previzualizare factura mascata</h2>
              <p className="mt-1 text-xs text-emerald-200">Generata local. Payload netransmis, factura neemisa, SPV neatins.</p>
            </div>
            <button type="button" onClick={() => setInvoicePreview(null)} title="Inchide previzualizarea" className="text-zinc-400 hover:text-white"><X size={18} /></button>
          </div>
          <div className="mt-4 grid gap-3 text-xs sm:grid-cols-2 lg:grid-cols-4">
            <SafeTestValue label="Validare" value={invoicePreview.valid ? "Trecuta" : "Necesita corectii"} ok={invoicePreview.valid} />
            <SafeTestValue label="Serie" value={invoicePreview.masked_payload?.seriesName || "-"} ok={Boolean(invoicePreview.masked_payload?.seriesName)} />
            <SafeTestValue label="Total plata" value={`${invoicePreview.product_total?.toFixed(2)} RON`} ok={invoicePreview.checks?.amount_matches_payment} />
            <SafeTestValue label="Licente" value={invoicePreview.license_count} ok={invoicePreview.license_count > 0} />
          </div>
          <div className="mt-4 grid gap-2 text-xs sm:grid-cols-2">
            {Object.entries(invoicePreview.checks || {}).map(([name, ok]) => (
              <p key={name} className={ok ? "text-emerald-300" : "text-amber-300"}>{ok ? "OK" : "VERIFICA"} - {name.replaceAll("_", " ")}</p>
            ))}
          </div>
          <pre className="mt-4 max-h-80 overflow-auto rounded bg-black/30 p-3 text-[11px] leading-relaxed text-zinc-300">{JSON.stringify(invoicePreview.masked_payload, null, 2)}</pre>
        </section>
      )}
      <div className="overflow-x-auto rounded-2xl border border-white/5 bg-[#121212]">
      <table className="w-full text-sm">
        <thead className="border-b border-white/5 text-left text-xs uppercase tracking-widest text-zinc-500">
          <tr>
            <th className="px-4 py-3">Când</th>
            <th className="px-4 py-3">Email</th>
            <th className="px-4 py-3">Sumă</th>
            <th className="px-4 py-3">Plată</th>
            <th className="px-4 py-3">Referință</th>
            <th className="px-4 py-3">Metodă</th>
            <th className="px-4 py-3">Licență</th>
            <th className="px-4 py-3">Factura SmartBill</th>
            <th className="px-4 py-3">Email documente</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.id} className="border-b border-white/5 last:border-0">
              <td className="px-4 py-3 font-mono text-xs text-zinc-500">{p.created_at?.slice(0, 16).replace("T", " ")}</td>
              <td className="px-4 py-3 text-zinc-300">{p.user_email}</td>
              <td className="px-4 py-3 font-mono">{p.amount?.toFixed(2)} {p.currency?.toUpperCase()}</td>
              <td className="px-4 py-3"><StatusPill status={p.payment_status} /></td>
              <td className="px-4 py-3 font-mono text-[10px] text-zinc-500">{p.bank_reference || p.session_id?.slice(-12)}</td>
              <td className="px-4 py-3 text-xs text-zinc-400">{p.payment_method || "card"}</td>
              <td className="px-4 py-3 font-mono text-[10px]">{p.license_id ? "✓" : "—"}</td>
              <td className="px-4 py-3 text-xs">
                {p.smartbill_invoice_series && p.smartbill_invoice_number ? (
                  <span className="font-mono text-emerald-300">{p.smartbill_invoice_series} {p.smartbill_invoice_number}</span>
                ) : (
                  <span className={p.smartbill_invoice_status === "failed" ? "text-red-300" : "text-zinc-500"} title={p.smartbill_invoice_error || ""}>
                    {p.smartbill_invoice_status || "neemisa"}
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-xs">
                <span
                  className={
                    p.document_email_status === "sent"
                      ? "text-emerald-300"
                      : p.document_email_status === "failed"
                        ? "text-red-300"
                        : "text-zinc-500"
                  }
                  title={p.document_email_error || ""}
                >
                  {p.document_email_status === "sent"
                    ? "trimis"
                    : p.document_email_status === "failed"
                      ? "esuat"
                      : "in asteptare"}
                </span>
              </td>
              <td className="px-4 py-3 text-right">
                {p.payment_method === "bank_transfer" && !p.license_id && (
                  <button onClick={() => confirmBank(p)} className="rounded-full bg-[#D4A23A] px-3 py-1.5 text-xs font-semibold text-black hover:bg-[#E2B85C]">Confirmă plata</button>
                )}
                {p.license_id && !p.smartbill_invoice_number && p.payment_method !== "legacy_migration" && (
                  <>
                    <button onClick={() => previewInvoice(p)} disabled={previewLoadingId === p.id} className="ml-2 rounded-full border border-[#D4A23A]/40 px-3 py-1.5 text-xs text-[#E2B85C] hover:bg-[#D4A23A]/10 disabled:opacity-60">
                      <Eye size={13} className="mr-1 inline" /> {previewLoadingId === p.id ? "Se genereaza..." : "Previzualizeaza"}
                    </button>
                    {p.smartbill_invoice_status === "failed" && (
                      <button onClick={() => retryInvoice(p)} disabled={previewLoadingId === p.id} className="ml-2 rounded-full bg-[#D4A23A] px-3 py-1.5 text-xs font-semibold text-black hover:bg-[#E2B85C] disabled:opacity-60">
                        Reincearca factura si emailul
                      </button>
                    )}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function SafeTestValue({ label, value, ok }) {
  return (
    <div>
      <p className="text-zinc-500">{label}</p>
      <p className={ok ? "mt-1 font-semibold text-emerald-300" : "mt-1 font-semibold text-amber-300"}>{value}</p>
    </div>
  );
}

/* ----- Audit ----- */
function AuditTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/audit").then(({ data }) => setItems(data)); }, []);
  return (
    <div className="overflow-x-auto rounded-2xl border border-white/5 bg-[#121212]">
      <table className="w-full text-sm">
        <thead className="border-b border-white/5 text-left text-xs uppercase tracking-widest text-zinc-500">
          <tr>
            <th className="px-4 py-3">Când</th>
            <th className="px-4 py-3">Actor</th>
            <th className="px-4 py-3">Acțiune</th>
            <th className="px-4 py-3">Țintă</th>
          </tr>
        </thead>
        <tbody>
          {items.map((a) => (
            <tr key={a.id} className="border-b border-white/5 last:border-0">
              <td className="px-4 py-3 font-mono text-xs text-zinc-500">{a.at?.slice(0, 19).replace("T", " ")}</td>
              <td className="px-4 py-3 text-zinc-300">{a.actor_email}</td>
              <td className="px-4 py-3 font-mono text-[#D4A23A]">{a.action}</td>
              <td className="px-4 py-3 font-mono text-xs text-zinc-500">{a.target?.slice(0, 12)}…</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
